import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import dinero from 'dinero.js';
import { useMemo } from 'react';
import { MetricCrossSubscriberEarningsResult } from '../../../../hooks/useSextforceMetricsCrossReferenceMetricEarnings';
import { OnlyFansMetricsGroupWithSummary } from '../../../../hooks/useSextforceMetricsGroups';
import { d2f } from '../../../../utils/common';
import SextforceMetricsGroupsTableRow from './SextforceMetricsGroupsTableRow';

type Props = {
    subscriber: any;
    groups: OnlyFansMetricsGroupWithSummary[];
    amountType: 'gross' | 'net';
    openGroupDialog: (metricId: string | null) => void;
    openShareDialog: (metricId: string | null) => void;
    handleDeleteGroup: (groupId: string) => void;
    timezone: string;
    crossPollinationEarningsMap: Map<string, MetricCrossSubscriberEarningsResult | null>;
    crossPollinationLoadingMap: Map<string, boolean>;
    crossPollinationEarningsSubscriberId: string;
};

const SextforceMetricsGroupsTable = (props: Props) => {
    const {
        subscriber,
        groups,
        amountType,
        openGroupDialog,
        openShareDialog,
        handleDeleteGroup,
        timezone,
        crossPollinationEarningsMap,
        crossPollinationLoadingMap,
        crossPollinationEarningsSubscriberId,
    } = props;

    const totals = useMemo(
        () =>
            (groups || []).reduce(
                (acc, group) => {
                    acc.metricsCount += group.metrics.length;
                    acc.subscribersTotal += group.subscribersTotal || 0;
                    acc.claimsTotal += group.claimsTotal || 0;
                    acc.clicksTotal += group.clicksTotal || 0;
                    acc.earningsTotal +=
                        group.earningsTotal && typeof group.earningsTotal !== 'number'
                            ? d2f(group.earningsTotal) * (amountType === 'net' ? 0.8 : 1)
                            : 0;
                    acc.crossPollinationEarningsTotal += group.metrics.reduce((sum, metric) => {
                        const metricEarnings = crossPollinationEarningsMap.get(metric._id);

                        return sum + (metricEarnings ? d2f(amountType === 'gross' ? metricEarnings.total : metricEarnings.totalNet) : 0);
                    }, 0);

                    return acc;
                },
                {
                    metricsCount: 0,
                    subscribersTotal: 0,
                    claimsTotal: 0,
                    clicksTotal: 0,
                    earningsTotal: 0,
                    crossPollinationEarningsTotal: 0,
                },
            ),
        [groups, amountType, crossPollinationEarningsMap],
    );

    const formatCurrency = (value: number) =>
        dinero({
            amount: Math.trunc(value * 100),
            currency: 'USD',
        }).toFormat();

    return (
        <Box
            component={'div'}
            sx={{
                width: '100%',
                overflowX: 'auto',
            }}
        >
            <Table
                width="100%"
                size="small"
                sx={{
                    // no left/right padding on the first and last cells
                    '& td:first-of-type, & th:first-of-type': {
                        // paddingLeft: 0,
                    },
                    // Bold header
                    '& th': {
                        fontWeight: 'bold',
                    },
                    // No wrapping
                    whiteSpace: 'nowrap',
                    '& td, & th': {
                        // Small padding on the cells
                        padding: 1,
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell width={45}>&nbsp;</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell align="center" width={70}>
                            Metrics
                        </TableCell>
                        <TableCell align="center" width={140}>
                            Subs. Total
                        </TableCell>
                        <TableCell align="center" width={140}>
                            Total Claimers
                        </TableCell>
                        <TableCell align="center" width={140}>
                            Total Clicks
                        </TableCell>
                        <TableCell align="center" width={150}>
                            Total {amountType === 'gross' ? 'Gross' : 'Net'}
                        </TableCell>
                        <TableCell align="center" width={150}>
                            Cross-Poll. Earnings {amountType === 'gross' ? 'Gross' : 'Net'}
                        </TableCell>
                        <TableCell align="center" width={140}>
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody
                    sx={{
                        // remove last bottom border
                        '& tr:last-child td': {
                            borderBottom: 'none',
                        },
                    }}
                >
                    {groups &&
                        groups.map(group => (
                            <SextforceMetricsGroupsTableRow
                                key={group._id}
                                subscriber={subscriber}
                                group={group}
                                amountType={amountType}
                                openGroupDialog={openGroupDialog}
                                openShareDialog={openShareDialog}
                                handleDeleteGroup={handleDeleteGroup}
                                timezone={timezone}
                                crossPollinationEarningsMap={crossPollinationEarningsMap}
                                crossPollinationLoadingMap={crossPollinationLoadingMap}
                                crossPollinationEarningsSubscriberId={crossPollinationEarningsSubscriberId}
                            />
                        ))}
                    <TableRow
                        sx={{
                            '& td': {
                                fontWeight: 'bold',
                                borderTop: theme => `2px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableCell>&nbsp;</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell align="center">{totals.metricsCount.toLocaleString()}</TableCell>
                        <TableCell align="right">{totals.subscribersTotal.toLocaleString()}</TableCell>
                        <TableCell align="right">{totals.claimsTotal.toLocaleString()}</TableCell>
                        <TableCell align="right">{totals.clicksTotal.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                            {formatCurrency(totals.earningsTotal)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                            {formatCurrency(totals.crossPollinationEarningsTotal)}
                        </TableCell>
                        <TableCell>&nbsp;</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    );
};

export default SextforceMetricsGroupsTable;
